import classNames from 'classnames';
import { uniqueId } from 'lodash-es';
import React, { DetailedHTMLProps, HTMLAttributes, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Calendar, useCalendarKeyboardShortcuts } from '@tcl-argon-foundations/calendar';
import { DateInput, useDateInputKeyboardShortcuts } from '@tcl-argon-foundations/date-input';
import { MonthYearInput } from '@tcl-argon-foundations/month-year-input';
import { useOnClickOutside } from '@tcl-argon-hooks/hooks';
import { Calendar as CalendarIcon } from '@tcl-argon-icons/icons';
import {
  partialDateFromString,
  BaseDatepickerProps,
  BaseInputProps,
  DateInputValue,
  InputTheme,
} from '@tcl-argon-models/models';
import { wrapN, mergeClasses } from '@tcl-argon-utils/utils';
import { Button } from '@tcl-argon-prefabs/button';
import { IconButton } from '@tcl-argon-prefabs/icon-button';
import { useSingleDatePickerStyles } from './styles';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface SingleDatePickerProps
  extends BaseInputProps,
    BaseDatepickerProps,
    Omit<DivElement, 'value' | 'onChange'> {
  /**
   * Render the `SingleDatePicker` as an icon button with no associated text input.
   * Useful in situations where textual date input takes up too much space or is unnecessary.
   */
  buttonOnly?: boolean;
  /** A handler called whenever the date changes */
  onChange: (value: DateInputValue) => void;
  /** The current `SingleDatepickerValue`. Valid dateStrings are formatted like so: MM/dd/yyyy */
  value: DateInputValue;
}

const useClearButtonStyles = createUseStyles({
  root: {
    flex: '1 1 50%',
  },
});

const DEFAULT_MIN_YEAR = 1920;

/**
 * A component allowing users to select a single date, with no associated time or timezone information.
 * This datepicker only concerns itself with days, months, and years.
 */
export const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  buttonOnly = false,
  classes: classOverrides,
  className,
  disabled,
  id,
  isOpen = false,
  label,
  maxYear = DEFAULT_MIN_YEAR + 110,
  minYear = DEFAULT_MIN_YEAR,
  onChange = () => {},
  onToggleIsOpen,
  theme = InputTheme.Default,
  value,
  placeholder = 'MM/DD/YYYY',
  ...props
}) => {
  [id] = useState(id || uniqueId('date_input_'));
  const classes = mergeClasses(useSingleDatePickerStyles(), classOverrides, { root: className });
  const clearButtonClasses = useClearButtonStyles();
  // The Calendar needs a month and year for first render but this month and year
  // are immediately replaced after first render by values set in an Effect below.
  const [calendarMonth, setCalendarMonth] = useState(1);
  const [calendarYear, setCalendarYear] = useState(2020);

  // On first render, set default month and year of Calendar from passed in value or today's date.
  useEffect(() => {
    const maybeDate = partialDateFromString(value.dateString);
    if (maybeDate.isValid) {
      setCalendarMonth(maybeDate.dateObject.month);
      setCalendarYear(maybeDate.dateObject.year);
    } else {
      const today = new Date();
      // JS Date months are zero-indexed
      setCalendarMonth(today.getMonth() + 1);
      setCalendarYear(today.getFullYear());
    }
  }, []);

  // When calendar is opened, if valid date has already been entered, show that date (unless it is already being shown.)
  useEffect(() => {
    if (isOpen) {
      const date = partialDateFromString(value.dateString);

      if (date.isValid) {
        const { month, year } = date.dateObject;

        if (month !== calendarMonth || year !== calendarYear) {
          setCalendarMonth(month);
          setCalendarYear(year);
        }
      }
    }
  }, [isOpen, value]);

  const { handleClear, handleDecrementDay, handleIncrementDay, handleSelectTodaysDate } = useDateInputKeyboardShortcuts(
    {
      onChange,
      value,
    },
  );

  const {
    handleDecrementDay: handleCalendarDecrementDay,
    handleDecrementWeek: handleCalendarDecrementWeek,
    handleIncrementDay: handleCalendarIncrementDay,
    handleIncrementWeek: handleCalendarIncrementWeek,
    handleSelectCurrentDate: handleCalendarSelectCurrentDate,
  } = useCalendarKeyboardShortcuts({
    onChange,
    value,
  });

  const mask = {
    date: true,
    datePattern: ['m', 'd', 'Y'],
    dateMin: `${minYear}-01-01`,
    dateMax: `${maxYear}-12-31`,
  };

  const clearSelectedDate = () => {
    onChange({
      dateString: '',
      dateObject: undefined,
    });
    onToggleIsOpen();
  };

  const onClickNextMonth = () => {
    if (calendarMonth + 1 === 13) {
      const nextYear = calendarYear + 1;
      if (nextYear <= maxYear) setCalendarYear(nextYear);
    }

    setCalendarMonth(wrapN(calendarMonth + 1, 1, 13));
  };

  const onClickPreviousMonth = () => {
    if (calendarMonth - 1 === 0) {
      const lastYear = calendarYear - 1;
      if (lastYear >= minYear) setCalendarYear(lastYear);
    }
    setCalendarMonth(wrapN(calendarMonth - 1, 1, 13));
  };

  const isDateSelected = (date: string) => date === value.dateString;

  const onSelectDateByCalendar = (date: string) => {
    onChange({
      dateObject: partialDateFromString(date).dateObject,
      dateString: date,
    });
  };

  const [ref] = useOnClickOutside(() => {
    if (isOpen) {
      onToggleIsOpen();
    }
  });

  return (
    <div className={classes.root} ref={ref} data-analytics="single-date-picker" {...props}>
      {buttonOnly ? (
        <IconButton
          Icon={CalendarIcon}
          className={classes.buttonOnly}
          disabled={disabled}
          isInverted={!isOpen}
          name={label}
          onClick={onToggleIsOpen}
        ></IconButton>
      ) : (
        <>
          <DateInput
            classes={classes}
            dateParser={partialDateFromString}
            disabled={disabled}
            forceFocusHighlight={isOpen}
            id={id}
            label={label}
            mask={mask}
            onChange={onChange}
            onClear={handleClear}
            onDecrementDay={handleDecrementDay}
            onIncrementDay={handleIncrementDay}
            onSelectTodaysDate={handleSelectTodaysDate}
            placeholder={placeholder}
            theme={theme}
            value={value}
          />
          <IconButton
            isInverted={!isOpen}
            Icon={CalendarIcon}
            name="toggle calendar"
            type="button"
            disabled={disabled}
            onClick={onToggleIsOpen}
            className={classes.calendarToggle}
          />
        </>
      )}
      <div
        className={classNames(classes.calendarContainer, {
          [classes.open]: isOpen && !disabled,
        })}
      >
        <MonthYearInput
          maxYear={maxYear}
          minYear={minYear}
          month={calendarMonth}
          onClickNextMonth={onClickNextMonth}
          onClickPreviousMonth={onClickPreviousMonth}
          onSelectYear={setCalendarYear}
          year={calendarYear}
        />
        <Calendar
          className={classes.calendarInput}
          isDateSelected={isDateSelected}
          month={calendarMonth}
          onDecrementDay={handleCalendarDecrementDay}
          onDecrementWeek={handleCalendarDecrementWeek}
          onIncrementDay={handleCalendarIncrementDay}
          onIncrementWeek={handleCalendarIncrementWeek}
          onSelectCurrentDate={handleCalendarSelectCurrentDate}
          onSelectDate={onSelectDateByCalendar}
          year={calendarYear}
        ></Calendar>
        <div className={classes.calendarButtonContainer}>
          <Button classes={clearButtonClasses} theme={InputTheme.Default} onClick={clearSelectedDate}>
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};
